import { Loader, ScrollArea } from '@mantine/core';
import { useMergedRef } from '@mantine/hooks';
import { FC, RefObject, useEffect, useMemo } from 'react';
import useSWRImmutable from 'swr/immutable';

import CalendarColumn from '@/components/CalendarView/CalendarColumn.tsx';
import Timeline from '@/components/CalendarView/Timeline.tsx';
import Paragraph from '@/components/text/Paragraph.tsx';

import { useAppointmentsForDateRange } from '@/hooks/use-appointments-for-date-range.ts';
import { useCalendarScroll } from '@/hooks/useCalendarScroll.ts';
import useConfig from '@/hooks/useConfig.ts';
import { useEnrichedPractitionerData } from '@/hooks/useEnrichedPractitionerData.ts';

import { DailyAppointment } from '@/models/appointment/Appointment.ts';

import { fetchScheduleTypeColorMap } from '@/services/appointmentService.ts';

import { useCalendarStore } from '@/stores/calendarStore.ts';
import { useFilterStore } from '@/stores/filterStore.ts';

import {
    BASE_HEADER_HEIGHT,
    CalendarColumnType,
    WHOLE_DAY_APPOINTMENT_HEIGHT,
    calculateContainerHeight,
    calculateMaxAllDayAppointments,
    generateDateRange,
    generateTimeSlots,
    getBookedCalendarIds,
    groupAppointmentsByCalendar,
    groupAppointmentsByDate,
    groupAppointmentsByDateAndOverlaps,
    groupOverlappingAppointments,
} from '@/utils/calendarUtils.ts';
import { dayjs } from '@/utils/dayjsSetup.ts';

interface Props {
    horizontalScrollRef: RefObject<HTMLDivElement>;
}

const CalendarView: FC<Props> = ({ horizontalScrollRef }) => {
    const { location_key, customer_id, instance_ids } = useConfig();
    // Get selected calendars directly from the filter store
    const is_week_view_enabled = useCalendarStore((state) => state.is_week_view_enabled);
    const is_only_booked_visible = useCalendarStore((state) => state.is_only_booked_visible);
    const timeline_interval = useCalendarStore((state) => state.timeline_interval);
    const calendars = useFilterStore((state) => state.selected_calendars);
    const start_date = useFilterStore((state) => state.start_date);
    const end_date = useFilterStore((state) => state.end_date);
    const selected_calendars = calendars[location_key] ?? [];
    const { data, calendars: all_calendars } = useEnrichedPractitionerData();
    const { appointments, is_loading, is_complete, mutate } = useAppointmentsForDateRange(start_date, end_date);

    const time_slots = generateTimeSlots(timeline_interval);
    const total_slots = (24 * 60) / timeline_interval;
    const container_height = calculateContainerHeight(timeline_interval);

    const { calendar_scroll_ref, scrollToTime } = useCalendarScroll(container_height);
    const mergedRef = useMergedRef(horizontalScrollRef, calendar_scroll_ref);
    const date_range = useMemo(() => generateDateRange(start_date, end_date), [start_date, end_date]);
    const setHeaderHeight = useCalendarStore((state) => state.setHeaderHeight);

    const { data: schedule_type_color_map, isLoading: is_schedule_type_color_map_loading } = useSWRImmutable<
        Record<string, string>
    >(['schedule-types', customer_id, instance_ids], () => fetchScheduleTypeColorMap(customer_id, instance_ids));

    const timeslot_height = container_height / total_slots;

    // Group appointments by calendar
    const appointments_by_calendar = useMemo(() => {
        if (!appointments) return {};
        return groupAppointmentsByCalendar(appointments);
    }, [appointments]);

    const getAppointmentsByDate = (): Record<string, DailyAppointment[]> => {
        if (!appointments || !is_week_view_enabled) return {};

        return groupAppointmentsByDate(appointments, selected_calendars);
    };

    const appointments_by_date = getAppointmentsByDate();

    const grouped_appointments_by_date = useMemo(() => {
        if (!is_week_view_enabled) return {};

        return groupAppointmentsByDateAndOverlaps(appointments_by_date);
    }, [appointments_by_date, is_week_view_enabled]);

    const isBookedFilterActive = is_only_booked_visible && !is_week_view_enabled && !is_loading && is_complete;
    const bookedCalendarIds = getBookedCalendarIds(appointments);

    // Filter calendars based on selection
    const filtered_calendars = isBookedFilterActive
        ? all_calendars.filter((calendar) => bookedCalendarIds.has(calendar.id))
        : all_calendars.filter((calendar) => selected_calendars.includes(calendar.id));

    useEffect(() => {
        if (!is_loading && filtered_calendars.length > 0 && start_date && end_date) {
            const today = dayjs().startOf('day');
            const start = dayjs(start_date);
            const end = dayjs(end_date);
            const is_today_in_range = today.isBetween(start, end, 'day', '[]');

            if (is_today_in_range) {
                const current_time = dayjs();
                scrollToTime(current_time.hour(), current_time.minute());
            } else if (appointments && appointments.length > 0) {
                // Find the earliest appointment
                let earliest_appointment = appointments[0];
                for (const appointment of appointments) {
                    if (dayjs(appointment.start).isBefore(dayjs(earliest_appointment.start))) {
                        earliest_appointment = appointment;
                    }
                }

                const appointment_time = dayjs(earliest_appointment.start);
                scrollToTime(appointment_time.hour(), appointment_time.minute());
            }
        }
    }, [is_loading, filtered_calendars, scrollToTime, start_date, end_date, appointments]);

    // Calculate max number of invalid appointments per column
    const max_invalid_appointments = useMemo(() => {
        if (!start_date || !appointments || !end_date) return 0;
        return calculateMaxAllDayAppointments(
            appointments,
            start_date,
            end_date,
            is_week_view_enabled,
            selected_calendars,
            filtered_calendars,
            appointments_by_calendar
        );
    }, [
        appointments,
        start_date,
        end_date,
        is_week_view_enabled,
        selected_calendars,
        filtered_calendars,
        appointments_by_calendar,
    ]);

    useEffect(() => {
        // Calculate header height based on invalid appointments
        // Calculate height based on number of rows needed
        // Assuming we can fit about 3 appointments per row with some margin
        const rows_needed = Math.ceil(max_invalid_appointments / 3);

        const calculated_header_height = BASE_HEADER_HEIGHT + rows_needed * WHOLE_DAY_APPOINTMENT_HEIGHT;
        setHeaderHeight(calculated_header_height);
    }, [max_invalid_appointments, BASE_HEADER_HEIGHT, WHOLE_DAY_APPOINTMENT_HEIGHT]);

    const single_calendar_timings = useMemo(() => {
        return selected_calendars.length === 1
            ? filtered_calendars.find((cal) => cal.id === selected_calendars[0])?.timings
            : undefined;
    }, [selected_calendars, filtered_calendars]);

    if (is_loading || is_schedule_type_color_map_loading) {
        return (
            <div className="flex h-full w-full items-center justify-center">
                <Loader />
            </div>
        );
    }

    if (filtered_calendars.length === 0) {
        return (
            <div className="flex h-full w-full items-center justify-center">
                <Paragraph className="text-slate-500">
                    {isBookedFilterActive ? 'Keine gebuchten Kalender an diesem Tag' : 'Keine Kalender ausgewählt'}
                </Paragraph>
            </div>
        );
    }

    return (
        <ScrollArea
            className="relative max-h-[85vh] w-full overflow-auto smallScreenHeight:max-h-[80vh]"
            viewportRef={mergedRef}
            type="always"
            styles={{ viewport: { containerType: 'inline-size' } }}
        >
            <div className="relative flex" style={{ minWidth: '100%', height: `${container_height}px` }}>
                <Timeline timeSlotHeight={timeslot_height} timeSlots={time_slots} />
                {is_week_view_enabled
                    ? date_range.map((date) => {
                          // For week view, find the practitioner for the single selected calendar
                          const week_practitioner =
                              selected_calendars.length === 1
                                  ? data
                                        ?.flatMap((d) => d.practitioners)
                                        ?.find((p) => p.calendars.some((c) => c.pvs.id === selected_calendars[0]))
                                  : undefined;

                          return (
                              <CalendarColumn
                                  key={dayjs(date).format('YYYY-MM-DD')}
                                  date={date}
                                  appointments={appointments_by_date[dayjs(date).format('YYYY-MM-DD')] || []}
                                  timeslotHeight={timeslot_height}
                                  containerHeight={container_height}
                                  timings={single_calendar_timings}
                                  timeSlots={time_slots}
                                  columnType={CalendarColumnType.WEEK}
                                  allCalendars={all_calendars}
                                  groupedAppointments={
                                      grouped_appointments_by_date[dayjs(date).format('YYYY-MM-DD')] || {}
                                  }
                                  scheduleTypeColorMap={schedule_type_color_map ?? {}}
                                  mutateAppointments={mutate}
                                  practitioner={week_practitioner}
                              />
                          );
                      })
                    : filtered_calendars.map((calendar) => {
                          // Group overlapping appointments for day view too
                          const calendar_appointments = appointments_by_calendar[calendar.id] || [];
                          const grouped_calendar_appointments = groupOverlappingAppointments(calendar_appointments);

                          // For day view, find the practitioner that owns this calendar
                          const day_practitioner = data
                              ?.flatMap((d) => d.practitioners)
                              ?.find((p) => p.calendars.some((c) => c.pvs.id === calendar.id));

                          return (
                              <CalendarColumn
                                  key={calendar.id}
                                  date={start_date}
                                  columnTitle={calendar.name}
                                  appointments={calendar_appointments}
                                  timeslotHeight={timeslot_height}
                                  containerHeight={container_height}
                                  timings={calendar.timings}
                                  timeSlots={time_slots}
                                  calendar={calendar}
                                  columnType={CalendarColumnType.DAY}
                                  groupedAppointments={grouped_calendar_appointments}
                                  scheduleTypeColorMap={schedule_type_color_map ?? {}}
                                  mutateAppointments={mutate}
                                  practitioner={day_practitioner}
                              />
                          );
                      })}
            </div>
        </ScrollArea>
    );
};

export default CalendarView;
